import React from 'react';
import { styled } from '@linaria/react';

import { normalize } from '@/utils';

import { usePointerInputMechanism } from './SatLitGrid.helpers';

interface Props {
  hue: number;
  saturation: number;
  lightness: number;
  size: number;
  fadeDelay: number;
  onChange: (params: {
    saturation: number;
    lightness: number;
  }) => void;
}

const SatLitGrid = ({
  hue,
  saturation,
  lightness,
  size,
  fadeDelay,
  onChange,
}: Props) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const { isTracking, startTracking } = usePointerInputMechanism({
    wrapperRef,
    onChange,
  });

  function handleKeyDown(ev: React.KeyboardEvent<HTMLButtonElement>) {
    let satIncrement = 0;
    let litIncrement = 0;

    switch (ev.key) {
      case 'ArrowUp': {
        ev.preventDefault();
        litIncrement = 0.05;
        break;
      }
      case 'ArrowDown': {
        ev.preventDefault();
        litIncrement = -0.05;
        break;
      }
      case 'ArrowLeft': {
        ev.preventDefault();
        satIncrement = -0.05;
        break;
      }
      case 'ArrowRight': {
        ev.preventDefault();
        satIncrement = 0.05;
        break;
      }
    }

    onChange({
      saturation: saturation + satIncrement,
      lightness: lightness + litIncrement,
    });
  }

  const indicatorSize = 24;
  const indicatorBuffer = indicatorSize * 0.25;

  const x = normalize(
    saturation,
    0,
    1,
    indicatorBuffer,
    size - indicatorBuffer
  );
  const y = normalize(
    lightness,
    0,
    1,
    size - indicatorBuffer,
    indicatorBuffer
  );

  return (
    <Wrapper>
      <GridWrapper
        ref={wrapperRef}
        onMouseDown={startTracking}
        onTouchStart={startTracking}
        style={{
          width: size,
          height: size,
          '--hue': hue + 'deg',
          cursor: isTracking ? 'none' : undefined,
          '--fade-delay': fadeDelay + 'ms',
        }}
      >
        <BackgroundGradient />
        <ColorGradient />
        <Indicator
          style={{
            '--size': `${indicatorSize}px`,
            '--x': `${x}px`,
            '--y': `${y}px`,
            background: `transparent`,
            cursor: isTracking ? 'none' : undefined,
          }}
          onKeyDown={handleKeyDown}
        />
      </GridWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const GridWrapper = styled.div`
  position: relative;
  border-radius: 4px;
  border: var(--white-border-thickness) solid white;
  touch-action: none;
  box-shadow: var(--shadow-elevation-medium);
  animation: fadeIn 250ms both;
  animation-delay: var(--fade-delay);
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  border-radius: 2px;
`;

const BackgroundGradient = styled(GradientOverlay)`
  background: linear-gradient(
    to top,
    hsl(0deg 0% 0%),
    hsl(0deg 0% 100%)
  );
`;

const ColorGradient = styled(GradientOverlay)`
  mix-blend-mode: overlay;
  background: linear-gradient(
    to right,
    hsl(var(--hue) 100% 50% / 0),
    hsl(var(--hue) 100% 50% / 1)
  );
`;

const Indicator = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: var(--size);
  height: var(--size);
  border: 2px solid black;
  border-radius: 50%;
  transform: translate(
    calc(-50% + var(--x) - var(--white-border-thickness)),
    calc(-50% + var(--y) - var(--white-border-thickness))
  );
  transform-origin: center center;
  cursor: grab;
  user-select: none;
  box-shadow: inset 0px 0px 0px 2px white;

  &:active {
    cursor: none;
  }

  &:focus {
    outline-offset: 4px;
  }
`;

export default SatLitGrid;
